<template>
  <div class="candidate-contact-chat">
    <div v-if="loading" class="candidate-contact-chat__loader">
      <VProgressCircular
        indeterminate
        color="blue"
        :size="32"
        :width="3"
      />
    </div>

    <VAlert v-else-if="fetchError" type="error" dense>
      {{ fetchError }}
    </VAlert>

    <div v-else class="candidate-contact-chat__card">
      <div class="candidate-contact-chat__header">
        <span v-if="unreadMessagesCount > 0" class="unread-badge">
          {{ unreadMessagesCount }} {{ $t('unread') }}
        </span>
      </div>

      <div ref="messagesList" class="messages-list huntica-nice-scroll">
        <div v-if="messages.length === 0" class="empty-messages-state">
          <img
            class="empty-messages-state__icon"
            :src="emptyChatIcon"
            alt=""
            width="60"
            height="60"
          >
          <p class="empty-messages-state__text">
            {{ $t('emptyMessagesLine1') }}<br>
            {{ $t('emptyMessagesLine2') }}
          </p>
        </div>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="message-row"
          :class="([
            msg.authorType === employerAuthorType ? 'message-row--right' : 'message-row--left',
            isUnread(msg) ? 'message-row--unread' : ''
          ])"
        >
          <div class="message-row__inner">
            <div
              class="message-bubble"
              :class="msg.authorType === employerAuthorType ? 'message-bubble--employer' : 'message-bubble--candidate'"
            >
              <p v-for="(line, i) in splitLines(msg.message)" :key="i">
                {{ line }}
              </p>
              <div v-if="msg.authorType === employerAuthorType" class="message-bubble__meta">
                <span class="message-bubble__meta-icon">
                  <svg
                    width="17"
                    height="12"
                    viewBox="0 0 17 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M3.90023 10.7125C3.73305 10.7125 3.56751 10.6796 3.41307 10.6156C3.25863 10.5516 3.11834 10.4578 3.00023 10.3395L0.219727 7.56045L1.28073 6.49995L3.90023 9.11945L10.7197 2.29995L11.7807 3.36045L4.80023 10.3395C4.68211 10.4578 4.54182 10.5516 4.38738 10.6156C4.23295 10.6796 4.0674 10.7125 3.90023 10.7125Z" fill="white" />
                    <path d="M8.9002 10.7123C8.73303 10.7124 8.56748 10.6795 8.41305 10.6155C8.25861 10.5515 8.11832 10.4576 8.0002 10.3393L6.95753 9.29718L7.99021 8.21L8.0816 8.30071L8.9002 9.11931L15.7197 2.2998L16.7807 3.3603L9.8002 10.3393C9.68209 10.4576 9.5418 10.5515 9.38736 10.6155C9.23292 10.6795 9.06738 10.7124 8.9002 10.7123Z" fill="white" />
                  </svg>
                </span>
                <span class="message-bubble__meta-time">
                  {{ formatDate(msg.createdAt) }}
                </span>
              </div>
              <div v-if="msg.authorType !== employerAuthorType" class="message-bubble__meta message-bubble__meta--candidate">
                <span class="message-bubble__meta-time message-bubble__meta-time--candidate">
                  {{ formatDate(msg.createdAt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showNewMessagesBanner"
        class="new-messages-banner"
        @click="onNewMessagesBannerClick"
      >
        {{ $t('newMessage') }} (+{{ newPolledMessagesCount }})
      </div>

      <div class="reply-block">
        <div class="reply-block__inner">
          <textarea
            ref="replyTextarea"
            v-model="replyText"
            class="reply-block__textarea"
            :class="{ 'reply-block__textarea--empty': messages.length === 0 }"
            :placeholder="messages.length === 0 ? $t('emptyMessagePlaceholder') : $t('messagePlaceholder')"
            autocomplete="off"
            rows="3"
          />

          <div class="reply-block__actions">
            <div class="reply-block__checkboxes">
              <VCheckbox
                v-model="emailCheckbox"
                hide-details
                class="reply-block__email-checkbox"
                :disabled="!candidate.email"
                label="Email"
              />

              <!--
              <VCheckbox
                v-model="smsCheckbox"
                hide-details
                class="reply-block__sms-checkbox"
                :disabled="!candidate.phone"
                label="SMS"
              />
              -->
            </div>

            <VBtn
              class="reply-block__send-button"
              :loading="sending"
              :disabled="!isSubmitButtonAvailable"
              @click="sendMessage"
            >
              {{ $t('send') }}
            </VBtn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  nextTick,
  getCurrentInstance,
} from '@vue/composition-api';
import Candidate from '@/orm/models/Candidate/Candidate';
import useCandidateCommunication from '@/compositions/useCandidateCommunication';
import useRecaptcha from '@/compositions/useRecaptcha';
import { mdiCheck, mdiCheckAll } from '@mdi/js';

export default {
  name: 'CandidateContactChatForm',
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    emailText: {
      type: String,
      default: '',
    },
    smsText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const instance = getCurrentInstance();
    const vm = instance?.proxy || instance;
    const t = (key, fallback = '') => vm?.$t?.(key) || fallback;
    const communicationApi = useCandidateCommunication();
    const { getRecaptchaToken } = useRecaptcha();

    const messagesList = ref(null);
    const replyTextarea = ref(null);
    const emptyChatIcon = require('@/assets/img/candidate/chat-empty.svg');

    const loading = ref(true);
    const sending = ref(false);
    const fetchError = ref(null);
    const messages = ref([]);
    const messagesPollingIntervalId = ref(null);
    const messagesPollingInProgress = ref(false);
    const unreadMessagesCount = ref(0);
    const unreadFromMessageIndex = ref(-1);
    const newPolledMessagesCount = ref(0);
    const emailCheckbox = ref(true);
    // const smsCheckbox = ref(false);
    // const smsMessage = ref(normalizeTemplate(props.smsText));
    const activeCommunicationCode = ref(null);

    const employerAuthorType = 'employer';

    function normalizeTemplate(text) {
      const source = text || '';
      return source.replace(/\n{3,}/g, '\n\n');
    }

    const replyText = ref(normalizeTemplate(props.emailText));

    const communicationCodes = computed(() => {
      const raw = [
        props.candidate.communicationCode,
        props.candidate.candidateCommunicationCode,
        props.candidate.publicToken?.token,
        props.candidate.hashId,
      ].filter(Boolean);

      const url = props.candidate.publicToken?.url;
      if (url && typeof url === 'string') {
        const parts = url.split('/').filter(Boolean);
        const tail = parts[parts.length - 1];
        if (tail) {
          raw.push(tail);
        }
      }

      return Array.from(new Set(raw));
    });

    function isMessageRead(msg) {
      const lastRead = props.candidate?.lastMessageReadAt;
      if (!lastRead || !msg?.createdAt) {
        return false;
      }

      const readDate = new Date(lastRead);
      const createdDate = new Date(msg.createdAt);
      if (Number.isNaN(readDate.getTime()) || Number.isNaN(createdDate.getTime())) {
        return false;
      }

      return createdDate.getTime() <= readDate.getTime();
    }

    const candidateName = computed(() => {
      const fullName = `${props.candidate.firstName || ''} ${props.candidate.lastName || ''}`.trim();
      return fullName || t('candidate', 'Candidate');
    });

    const isSubmitButtonAvailable = computed(() => {
      return Boolean(replyText.value && replyText.value.trim());
    });

    function stopMessagesPolling() {
      if (messagesPollingIntervalId.value) {
        clearInterval(messagesPollingIntervalId.value);
        messagesPollingIntervalId.value = null;
      }
    }

    function scrollToBottom(smooth = false) {
      const list = messagesList.value;
      if (list) {
        if (smooth) {
          list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
        } else {
          list.scrollTop = list.scrollHeight;
        }
      }
    }

    function isAtBottom() {
      const list = messagesList.value;
      if (!list) return false;
      return list.scrollHeight - list.scrollTop - list.clientHeight < 20;
    }

    async function fetchMessages(isPolling = false) {
      if (messagesPollingInProgress.value || !props.candidate.id) {
        return;
      }

      messagesPollingInProgress.value = true;
      try {
        const prevCount = messages.value.length;
        const wasAtBottom = isAtBottom();
        const sorted = await communicationApi.fetchCommunicationMessagesByCandidate(props.candidate.id, {
          page: 1,
          perPage: 50,
        });

        if (isPolling && prevCount > 0) {
          const prevLast = prevCount ? messages.value[prevCount - 1] : null;
          const prevLastId = prevLast ? prevLast.id : null;

          let added = 0;

          if (prevLastId != null) {
            const idx = sorted.findIndex((m) => m.id === prevLastId);
            if (idx === -1) {
              const prevIds = new Set(messages.value.map((m) => m.id));
              added = sorted.reduce((acc, m) => acc + (prevIds.has(m.id) ? 0 : 1), 0);
            } else {
              added = sorted.length - (idx + 1);
            }
          } else {
            added = sorted.length - prevCount;
          }

          if (added > 0) {
            if (wasAtBottom) {
              nextTick(() => {
                scrollToBottom();
                markAsRead();
              });
            } else {
              newPolledMessagesCount.value += added;
            }
          }
        }

        const updatedMessages = sorted.map((msg) => ({
          ...msg,
          isRead: msg.readAt !== null,
        }));
        messages.value = updatedMessages;

        if (unreadMessagesCount.value > 0) {
          const candidateMessages = messages.value.filter((m) => m.authorType !== employerAuthorType);
          const unreadStart = candidateMessages[candidateMessages.length - unreadMessagesCount.value];
          unreadFromMessageIndex.value = unreadStart ? messages.value.indexOf(unreadStart) : -1;
        }
      } finally {
        messagesPollingInProgress.value = false;
      }
    }

    async function pollMessages() {
      try {
        await fetchMessages(true);
      } catch (e) {
        // silent fail while polling
      }
    }

    function startMessagesPolling() {
      stopMessagesPolling();
      messagesPollingIntervalId.value = setInterval(() => {
        pollMessages();
      }, 30000);
    }

    async function markAsRead() {
      try {
        await communicationApi.markAsRead(props.candidate.id);
        unreadFromMessageIndex.value = -1;
        unreadMessagesCount.value = 0;
      } catch (e) {
        // silent fail
      }
    }

    async function fetchData() {
      loading.value = true;
      fetchError.value = null;
      try {
        activeCommunicationCode.value = communicationCodes.value[0] || null;

        if (!props.candidate.id) {
          unreadMessagesCount.value = 0;
          messages.value = [];
          return;
        }

        await fetchMessages();
        // Не отмечаем как прочитанные автоматически
        newPolledMessagesCount.value = 0;
      } catch (e) {
        fetchError.value = e?.message || t('fetchError', 'Failed to load communication');
      } finally {
        loading.value = false;
      }
    }

    async function onNewMessagesBannerClick() {
      newPolledMessagesCount.value = 0;
      nextTick(() => scrollToBottom(true));
      await markAsRead();
    }

    function isUnread(msg) {
      if (unreadFromMessageIndex.value === -1) return false;
      const idx = messages.value.indexOf(msg);
      return idx >= unreadFromMessageIndex.value && msg.authorType !== employerAuthorType;
    }

    async function sendMessage() {
      const message = (replyText.value || '').trim();
      if (!message) return;

      sending.value = true;
      try {
        newPolledMessagesCount.value = 0;

        const recaptchaToken = await getRecaptchaToken('candidateContact');

        const payload = {
          recaptchaToken,
          mail: emailCheckbox.value ? message : null,
          // sms: smsCheckbox.value ? message : null,
          sms: null,
        };

        await Candidate.api().notify(props.candidate.id, payload);

        await fetchMessages();
        nextTick(() => scrollToBottom());

        replyText.value = '';
        vm?.$toast?.success(t('messageSent', 'Message sent'));
      } catch (e) {
        vm?.$toast?.error(e?.message || t('sendError', 'Send error'));
      } finally {
        sending.value = false;
      }
    }

    function formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }

    function splitLines(text) {
      return (text || '').split('\n').filter((line) => line.trim() !== '');
    }

    function onMessagesListScroll() {
      if (isAtBottom() && newPolledMessagesCount.value > 0) {
        newPolledMessagesCount.value = 0;
        markAsRead();
      }
    }

    const showNewMessagesBanner = computed(() => {
      return newPolledMessagesCount.value > 0;
    });

    onMounted(async () => {
      await fetchData();
      if (props.candidate.id) {
        startMessagesPolling();
      }
      nextTick(() => {
        scrollToBottom();
        const list = messagesList.value;
        if (list) {
          list.addEventListener('scroll', onMessagesListScroll);
        }
      });
    });

    onBeforeUnmount(() => {
      stopMessagesPolling();
      const list = messagesList.value;
      if (list) {
        list.removeEventListener('scroll', onMessagesListScroll);
      }
    });

    onBeforeUnmount(() => {
      stopMessagesPolling();
    });

    return {
      showNewMessagesBanner,
      communicationApi,
      messagesList,
      replyTextarea,
      emptyChatIcon,
      loading,
      sending,
      fetchError,
      messages,
      unreadMessagesCount,
      newPolledMessagesCount,
      replyText,
      emailCheckbox,
      employerAuthorType,
      communicationCodes,
      activeCommunicationCode,
      candidateName,
      isSubmitButtonAvailable,
      startMessagesPolling,
      stopMessagesPolling,
      fetchMessages,
      pollMessages,
      fetchData,
      scrollToBottom,
      markAsRead,
      onNewMessagesBannerClick,
      isUnread,
      isMessageRead,
      sendMessage,
      formatDate,
      splitLines,
      normalizeTemplate,
      mdiCheck,
      mdiCheckAll,
    };
  },
};
</script>

<style scoped lang="scss">
.candidate-contact-chat {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.candidate-contact-chat__loader {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
}

.candidate-contact-chat__card {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.candidate-contact-chat__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.candidate-contact-chat__title {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
}

.unread-badge {
  display: inline-flex;
  align-items: center;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.messages-list {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 23px;
  display: flex;
  flex-direction: column;
}

.message-row {
  display: flex;
  margin-bottom: 24px;

  &--unread .message-bubble--candidate {
    background: #dff0ff;
    box-shadow: 0 0 0 1px rgba(10, 130, 217, 0.2);
  }

  &--left {
    justify-content: flex-start;
  }

  &--right {
    justify-content: flex-end;
  }

  &__inner {
    max-width: 72%;
  }
}

.message-author {
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  margin-bottom: 4px;

  .message-row--right & {
    text-align: right;
  }
}

.message-bubble {
  padding: 12px 16px;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;

  &--employer {
    background: #0A82D9;
    border-radius: 16px 16px 4px 16px;
    padding: 16px 20px;
    margin-bottom: 4px;
  }

  &--candidate {
    background: #ffffff;
    border-radius: 16px 16px 16px 4px;
    padding: 16px 20px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 140%;
    color: #212121;
  }

  &--employer p {
    color: #ffffff;
    font-weight: 400;
  }

  &--candidate p {
    color: #212121;
    font-weight: 400;
  }
}

.message-bubble__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 4px;
  margin-top: 8px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  line-height: 120%;

  &--candidate {
    justify-content: flex-start;
    color: #757575;
    font-size: 10px;
    font-weight: 300;
    line-height: 120%;
    margin-top: 8px;
  }
}

.message-bubble__meta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.message-bubble__meta-time {
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  line-height: 120%;

  &--candidate {
    color: #757575;
    font-size: 10px;
    font-weight: 300;
    line-height: 120%;
  }
}

.message-time {
  margin-top: 6px;
  font-size: 10px;
  color: #9e9e9e;
}

.message-row--right .message-time {
  text-align: right;
}

.new-messages-banner {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  align-items: center;
  background: #0a82d9;
  color: #fff;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(10, 130, 217, 0.25);
  transition: background 0.2s;
  user-select: none;
  align-self: center;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background: #0870bb;
  }
}

.reply-block {
  flex-shrink: 0;
  margin-top: auto;
  border-top: 1px solid #e0e0e0;

  &__inner {
    padding: 20px;
    background: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__textarea {
    width: 100%;
    height: 110px;
    min-height: 110px;
    margin-bottom: 0;
    background: #ffffff;
    border: 1px solid #E0E0E0;
    border-radius: 4px;
    padding: 12px;
    box-sizing: border-box;
    font-size: 14px;
    font-weight: 400;
    color: #212121;
    line-height: 20px;
    resize: none;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;

    &--empty {
      height: 80px;
      min-height: 80px;
    }
  }

  &__textarea:focus {
    outline: none;
    box-shadow: none;
  }

  &__textarea::placeholder {
    color: #757575;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    opacity: 1;
  }

  &__actions {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-top: 12px;
  }

  &__checkboxes {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 12px 16px;
    flex: 1;
    min-width: 0;
  }

  &__email-checkbox {
    margin-top: 0;
    padding-top: 0;

    &::v-deep {
      .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
      }

      .v-input__slot {
        margin-bottom: 0;
      }

      .v-label {
        color: #212121;
        font-size: 14px;
        font-weight: 400;
        line-height: 1;
      }
    }
  }

  &__send-button {
    flex-shrink: 0;
    min-width: 0;
    height: 32px !important;
    min-height: 32px !important;
    background: #0A82D9 !important;
    color: #FFFFFF !important;
    padding: 0 16px !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    text-transform: none;
    letter-spacing: normal;
    border-radius: 6px !important;
    box-shadow: none;

    &::v-deep .v-btn__content {
      color: #FFFFFF;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
    }
  }
}

.empty-messages-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;

  &__icon {
    display: block;
    flex-shrink: 0;
  }

  &__text {
    margin: 20px 0 0;
    padding: 0;
    text-align: center;
    color: #B6C1C7;
    font-size: 20px;
    font-weight: 300;
    line-height: 110%;
  }
}
</style>

<i18n locale="en" lang="yaml">
  unread: unread
  newMessage: New message
  communicationNotCreatedYet: The candidate has not started communication yet. You can still send an email below.
  send: Send
  sendTo: Send to
  noEmail: no email
  messagePlaceholder: Your message to candidate
  emptyMessagesLine1: Your conversation with the candidate
  emptyMessagesLine2: will appear here
  emptyMessagePlaceholder: Your comment (Enter to send)
  messageSent: Message sent
  fetchError: Failed to load communication
  sendError: Failed to send message
  candidate: Candidate
  you: You
  missingCommunicationCode: Missing communication code
  readStatusRead: Read
  readStatusUnread: Not read
</i18n>

<i18n locale="ru" lang="yaml">
  unread: непрочитанных
  newMessage: Новое сообщение
  communicationNotCreatedYet: Кандидат еще не начал переписку. Вы можете отправить сообщение на email ниже.
  send: Отправить
  sendTo: Отправить на
  noEmail: email не указан
  messagePlaceholder: Ваше сообщение кандидату
  emptyMessagesLine1: Здесь будет переписка
  emptyMessagesLine2: с кандидатом
  emptyMessagePlaceholder: Ваш комментарий (Enter — отправка сообщения)
  messageSent: Сообщение отправлено
  fetchError: Ошибка загрузки переписки
  sendError: Ошибка отправки сообщения
  candidate: Кандидат
  you: Вы
  missingCommunicationCode: Отсутствует код переписки
  readStatusRead: Прочитано
  readStatusUnread: Не прочитано
</i18n>
